(function () {
  if (customElements.get('pongbot-business-form')) {
    return;
  }

  class PongbotBusinessForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.initialized = true;
      this.form = this.querySelector('form');
      this.categorySelect = this.querySelector('[data-pfb-category-select]');
      this.categoryField = this.categorySelect ? this.categorySelect.closest('[data-pfb-field]') : null;
      this.categoryDropdown = this.querySelector('[data-pfb-category-dropdown]');
      this.categoryTrigger = this.querySelector('[data-pfb-category-trigger]');
      this.categoryValue = this.querySelector('[data-pfb-category-value]');
      this.categoryList = this.querySelector('[data-pfb-category-list]');
      this.categoryOptions = Array.from(this.querySelectorAll('[data-pfb-category-option]'));
      this.panels = Array.from(this.querySelectorAll('[data-pfb-panel]'));
      this.steps = Array.from(this.querySelectorAll('[data-pfb-step-item]'));
      this.productPanels = Array.from(this.querySelectorAll('[data-pfb-products]'));
      this.experienceLabel = this.querySelector('[data-pfb-experience-label]');
      this.dealerQuestion = this.querySelector('[data-pfb-dealer-question]');
      this.industriesField = this.querySelector('[data-pfb-industries-value]');
      this.summaryField = this.querySelector('[data-pfb-summary]');
      this.postedState = this.querySelector('[data-pfb-posted-state]');
      this.submitError = this.querySelector('[data-pfb-submit-error]');
      this.nativeSubmitStorageKey = this.getNativeSubmitStorageKey();
      this.draftStorageKey = this.getDraftStorageKey();
      this.stepStorageKey = this.getStepStorageKey();
      this.hasNativeSubmitIntent = this.consumeNativeSubmitIntent();

      const hasPostedSuccess = this.postedState && this.postedState.getAttribute('data-pfb-posted-state') === 'success';
      if (hasPostedSuccess && this.hasNativeSubmitIntent) {
        this.clearDraft();
        this.step = 4;
      } else {
        const hasDraft = this.restoreDraft();
        const storedStep = this.getStoredStep();
        this.step = hasPostedSuccess && !hasDraft ? 1 : storedStep || 1;
      }

      if (hasPostedSuccess) {
        this.cleanPostedSuccessUrl();
      }

      this.bindEvents();
      this.enableCustomCategoryDropdown();
      this.updateCategoryState();
      this.setStep(this.step, { focus: false, scroll: false });
      this.syncMobileCategoryDropdownDefault();
    }

    disconnectedCallback() {
      if (this.boundDocumentClick) {
        document.removeEventListener('click', this.boundDocumentClick);
      }
      if (this.boundDocumentKeydown) {
        document.removeEventListener('keydown', this.boundDocumentKeydown);
      }
    }

    getNativeSubmitStorageKey() {
      const formId = this.form && this.form.id ? this.form.id : this.id || 'pongbot-business-form';
      return `pongbot-business-form:${window.location.pathname}:${formId}:native-submit`;
    }

    getDraftStorageKey() {
      const formId = this.form && this.form.id ? this.form.id : this.id || 'pongbot-business-form';
      return `pongbot-business-form:${window.location.pathname}:${formId}:draft`;
    }

    getStepStorageKey() {
      const formId = this.form && this.form.id ? this.form.id : this.id || 'pongbot-business-form';
      return `pongbot-business-form:${window.location.pathname}:${formId}:step`;
    }

    markNativeSubmitIntent() {
      try {
        window.sessionStorage.setItem(this.nativeSubmitStorageKey, 'true');
      } catch (error) {
        // Session storage can be unavailable in private browsing or strict modes.
      }
    }

    consumeNativeSubmitIntent() {
      try {
        const hasIntent = window.sessionStorage.getItem(this.nativeSubmitStorageKey) === 'true';
        window.sessionStorage.removeItem(this.nativeSubmitStorageKey);
        return hasIntent;
      } catch (error) {
        return false;
      }
    }

    getStoredStep() {
      let storedStep = null;

      try {
        storedStep = window.sessionStorage.getItem(this.stepStorageKey);
      } catch (error) {
        storedStep = null;
      }

      const step = Number(storedStep);
      return step >= 1 && step <= 3 ? step : null;
    }

    saveStoredStep() {
      if (!this.stepStorageKey || this.step >= 4) {
        return;
      }

      try {
        window.sessionStorage.setItem(this.stepStorageKey, String(this.step));
      } catch (error) {
        // Session storage can be unavailable in private browsing or strict modes.
      }
    }

    getPersistableFields() {
      if (!this.form) {
        return [];
      }

      return Array.from(this.form.querySelectorAll('input, select, textarea')).filter((field) => {
        const type = field.type;
        return type !== 'hidden' && type !== 'submit' && type !== 'button' && field !== this.summaryField;
      });
    }

    getFieldDraftKey(field) {
      if (field.name) {
        return field.name;
      }

      if (field.id) {
        return field.id;
      }

      if (field.hasAttribute('data-pfb-industry')) {
        return `industry:${field.value}`;
      }

      return null;
    }

    saveDraft() {
      if (!this.draftStorageKey) {
        return;
      }

      const fields = {};
      this.getPersistableFields().forEach((field) => {
        const key = this.getFieldDraftKey(field);
        if (!key) {
          return;
        }

        fields[key] = field.type === 'checkbox' ? Boolean(field.checked) : field.value;
      });

      try {
        window.sessionStorage.setItem(this.draftStorageKey, JSON.stringify({
          step: this.step >= 1 && this.step <= 3 ? this.step : 1,
          fields: fields,
          updatedAt: Date.now()
        }));
      } catch (error) {
        // Session storage can be unavailable in private browsing or strict modes.
      }
    }

    restoreDraft() {
      if (!this.draftStorageKey) {
        return false;
      }

      let draft = null;

      try {
        draft = JSON.parse(window.sessionStorage.getItem(this.draftStorageKey) || 'null');
      } catch (error) {
        draft = null;
      }

      if (!draft || !draft.fields) {
        return false;
      }

      let restored = false;

      this.getPersistableFields().forEach((field) => {
        const key = this.getFieldDraftKey(field);
        if (!key || !Object.prototype.hasOwnProperty.call(draft.fields, key)) {
          return;
        }

        if (field.type === 'checkbox') {
          field.checked = Boolean(draft.fields[key]);
        } else {
          field.value = draft.fields[key];
        }

        restored = true;
      });

      const draftStep = Number(draft.step);
      if (draftStep >= 1 && draftStep <= 3) {
        try {
          window.sessionStorage.setItem(this.stepStorageKey, String(draftStep));
        } catch (error) {
          // Session storage can be unavailable in private browsing or strict modes.
        }
      }

      return restored;
    }

    clearDraft() {
      try {
        window.sessionStorage.removeItem(this.draftStorageKey);
        window.sessionStorage.removeItem(this.stepStorageKey);
      } catch (error) {
        // Session storage can be unavailable in private browsing or strict modes.
      }
    }

    cleanPostedSuccessUrl() {
      if (!window.history || !window.URL) {
        return;
      }

      const url = new URL(window.location.href);
      if (!url.searchParams.has('contact_posted')) {
        return;
      }

      url.searchParams.delete('contact_posted');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    enableCustomCategoryDropdown() {
      if (!this.categoryField || !this.categoryDropdown || !this.categoryTrigger || !this.categoryList) {
        return;
      }

      this.categoryField.classList.add('is-custom-select-ready');
    }

    bindEvents() {
      this.querySelectorAll('[data-pfb-next]').forEach((button) => {
        button.addEventListener('click', () => {
          const targetStep = Number(button.getAttribute('data-pfb-next')) || this.step + 1;
          if (this.validateStep(this.step, true)) {
            this.setStep(targetStep);
          }
        });
      });

      this.querySelectorAll('[data-pfb-back]').forEach((button) => {
        button.addEventListener('click', () => {
          const targetStep = Number(button.getAttribute('data-pfb-back')) || this.step - 1;
          this.setStep(targetStep);
        });
      });

      if (this.categorySelect) {
        this.categorySelect.addEventListener('change', () => {
          this.updateCategoryState();
          this.clearFieldError(this.categorySelect);
          this.syncMobileCategoryDropdownDefault();
        });
      }

      if (this.categoryTrigger) {
        this.categoryTrigger.addEventListener('click', (event) => {
          event.stopPropagation();
          this.setCategoryDropdownOpen(!(this.categoryDropdown && this.categoryDropdown.classList.contains('is-open')));
        });
        this.categoryTrigger.addEventListener('keydown', (event) => this.handleCategoryTriggerKeydown(event));
      }

      this.categoryOptions.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.selectCategoryOption(button);
        });
        button.addEventListener('keydown', (event) => this.handleCategoryOptionKeydown(event, button));
      });

      this.boundDocumentClick = (event) => {
        if (this.categoryDropdown && !this.categoryDropdown.contains(event.target)) {
          this.setCategoryDropdownOpen(false);
        }
      };
      document.addEventListener('click', this.boundDocumentClick);

      this.boundDocumentKeydown = (event) => {
        if (event.key === 'Escape') {
          this.setCategoryDropdownOpen(false);
        }
      };
      document.addEventListener('keydown', this.boundDocumentKeydown);

      this.querySelectorAll('[data-pfb-required]').forEach((field) => {
        const eventName = field.tagName === 'SELECT' ? 'change' : 'input';
        field.addEventListener(eventName, () => {
          if (field.closest('[data-pfb-field]') && field.closest('[data-pfb-field]').classList.contains('is-invalid')) {
            this.validateField(field, false);
          }
        });
      });

      this.getPersistableFields().forEach((field) => {
        const eventName = field.tagName === 'SELECT' || field.type === 'checkbox' ? 'change' : 'input';
        field.addEventListener(eventName, () => this.saveDraft());
      });

      this.querySelectorAll('[data-pfb-industry]').forEach((checkbox) => {
        checkbox.addEventListener('change', () => this.updateIndustriesValue());
      });

      if (this.form) {
        this.form.setAttribute('novalidate', 'novalidate');
        this.form.addEventListener('submit', (event) => this.handleSubmit(event));
      }
    }

    setStep(step, options) {
      const settings = Object.assign({ focus: true, scroll: true }, options || {});
      this.step = Math.max(1, Math.min(4, Number(step) || 1));
      this.setAttribute('data-step', String(this.step));
      this.saveStoredStep();

      this.panels.forEach((panel) => {
        const panelStep = Number(panel.getAttribute('data-pfb-panel'));
        panel.hidden = panelStep !== this.step;
      });

      this.steps.forEach((item) => {
        const itemStep = Number(item.getAttribute('data-pfb-step-item'));
        item.classList.toggle('is-active', itemStep === this.step && this.step !== 4);
        item.classList.toggle('is-complete', itemStep < this.step || this.step === 4);
        item.classList.toggle('is-line-complete', itemStep < this.step);
        if (itemStep === this.step) {
          item.setAttribute('aria-current', 'step');
        } else {
          item.removeAttribute('aria-current');
        }
      });

      if (settings.focus) {
        const activePanel = this.panels.find((panel) => Number(panel.getAttribute('data-pfb-panel')) === this.step);
        let focusTarget = activePanel && activePanel.querySelector('select, input, textarea, button, a');
        if (this.step === 1 && this.categoryTrigger && window.matchMedia('(max-width: 767px)').matches) {
          focusTarget = this.categoryTrigger;
        }
        if (focusTarget) {
          focusTarget.focus({ preventScroll: true });
        }
      }

      if (settings.scroll && window.matchMedia('(max-width: 767px)').matches) {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      if (settings.persist !== false && this.step < 4) {
        this.saveDraft();
      }

      this.syncMobileCategoryDropdownDefault();
    }

    updateCategoryState() {
      const value = this.categorySelect ? this.categorySelect.value : '';
      const selectedOption = this.categorySelect ? this.categorySelect.options[this.categorySelect.selectedIndex] : null;

      if (this.categorySelect) {
        this.categorySelect.classList.toggle('has-value', Boolean(value));
      }

      this.classList.toggle('has-category', Boolean(value));
      this.updateCustomCategory(value, selectedOption);

      this.productPanels.forEach((panel) => {
        panel.hidden = panel.getAttribute('data-pfb-products') !== value;
      });

      if (selectedOption) {
        const experienceLabel = selectedOption.getAttribute('data-experience-label');
        const dealerQuestion = selectedOption.getAttribute('data-dealer-question');
        if (this.experienceLabel && experienceLabel) {
          this.experienceLabel.textContent = experienceLabel;
        }
        if (this.dealerQuestion && dealerQuestion) {
          this.dealerQuestion.textContent = dealerQuestion;
        }
      }
    }

    updateCustomCategory(value, selectedOption) {
      if (this.categoryDropdown) {
        this.categoryDropdown.classList.toggle('has-value', Boolean(value));
      }

      if (this.categoryValue && this.categorySelect) {
        const placeholder = this.categorySelect.options[0] ? this.categorySelect.options[0].textContent.trim() : '';
        this.categoryValue.textContent = value && selectedOption ? selectedOption.textContent.trim() : placeholder;
      }

      this.categoryOptions.forEach((button) => {
        const isSelected = button.getAttribute('data-pfb-category-option') === value;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      });
    }

    setCategoryDropdownOpen(open) {
      if (!this.categoryDropdown || !this.categoryTrigger || !this.categoryList) {
        return;
      }

      this.categoryDropdown.classList.toggle('is-open', Boolean(open));
      this.categoryTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.categoryList.hidden = !open;

      if (open) {
        const selectedOption = this.categoryOptions.find((button) => button.classList.contains('is-selected')) || this.categoryOptions[0];
        if (selectedOption && selectedOption.id) {
          this.categoryTrigger.setAttribute('aria-activedescendant', selectedOption.id);
        }
      } else {
        this.categoryTrigger.removeAttribute('aria-activedescendant');
      }
    }

    syncMobileCategoryDropdownDefault() {
      if (!this.categorySelect || !this.categoryDropdown || this.step !== 1) {
        return;
      }

      if (this.categorySelect.value) {
        this.setCategoryDropdownOpen(false);
      }
    }

    selectCategoryOption(button) {
      const nextValue = button.getAttribute('data-pfb-category-option') || '';
      if (this.categorySelect) {
        this.categorySelect.value = nextValue;
        this.categorySelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.setCategoryDropdownOpen(false);
      if (this.categoryTrigger) {
        this.categoryTrigger.focus({ preventScroll: true });
      }
    }

    focusCategoryOption(nextIndex) {
      if (!this.categoryOptions.length) {
        return;
      }

      const boundedIndex = Math.max(0, Math.min(this.categoryOptions.length - 1, nextIndex));
      const nextOption = this.categoryOptions[boundedIndex];
      if (nextOption) {
        nextOption.focus({ preventScroll: true });
        if (this.categoryTrigger && nextOption.id) {
          this.categoryTrigger.setAttribute('aria-activedescendant', nextOption.id);
        }
      }
    }

    handleCategoryTriggerKeydown(event) {
      if (!['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        return;
      }

      event.preventDefault();
      this.setCategoryDropdownOpen(true);
      const selectedIndex = this.categoryOptions.findIndex((button) => button.classList.contains('is-selected'));
      const fallbackIndex = event.key === 'ArrowUp' ? this.categoryOptions.length - 1 : 0;
      this.focusCategoryOption(selectedIndex >= 0 ? selectedIndex : fallbackIndex);
    }

    handleCategoryOptionKeydown(event, button) {
      const currentIndex = this.categoryOptions.indexOf(button);

      if (event.key === 'Escape') {
        event.preventDefault();
        this.setCategoryDropdownOpen(false);
        if (this.categoryTrigger) {
          this.categoryTrigger.focus({ preventScroll: true });
        }
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.selectCategoryOption(button);
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.focusCategoryOption(currentIndex + 1);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.focusCategoryOption(currentIndex - 1);
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        this.focusCategoryOption(0);
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();
        this.focusCategoryOption(this.categoryOptions.length - 1);
      }
    }

    validateStep(step, showErrors) {
      const panel = this.querySelector(`[data-pfb-panel="${step}"]`);
      if (!panel) {
        return true;
      }

      let isValid = true;
      panel.querySelectorAll('[data-pfb-required]').forEach((field) => {
        if (!this.validateField(field, showErrors)) {
          isValid = false;
        }
      });

      if (!isValid && showErrors) {
        const firstInvalid = panel.querySelector('.is-invalid input, .is-invalid select, .is-invalid textarea');
        if (firstInvalid) {
          const focusTarget = firstInvalid === this.categorySelect && this.categoryTrigger ? this.categoryTrigger : firstInvalid;
          if (firstInvalid === this.categorySelect) {
            this.setCategoryDropdownOpen(true);
          }
          focusTarget.focus({ preventScroll: true });
        }
      }

      return isValid;
    }

    validateField(field, showErrors) {
      const wrapper = field.closest('[data-pfb-field]');
      let value = field.value;
      let isValid = true;

      if (typeof value === 'string') {
        value = value.trim();
      }

      if (!value) {
        isValid = false;
      }

      if (field.type === 'email' && value && !field.validity.valid) {
        isValid = false;
      }

      if (wrapper) {
        wrapper.classList.toggle('is-invalid', showErrors && !isValid);
      }

      field.setAttribute('aria-invalid', isValid ? 'false' : 'true');
      if (field === this.categorySelect && this.categoryTrigger) {
        this.categoryTrigger.setAttribute('aria-invalid', isValid ? 'false' : 'true');
      }
      return isValid;
    }

    clearFieldError(field) {
      const wrapper = field.closest('[data-pfb-field]');
      if (wrapper) {
        wrapper.classList.remove('is-invalid');
      }
      field.setAttribute('aria-invalid', 'false');
      if (field === this.categorySelect && this.categoryTrigger) {
        this.categoryTrigger.setAttribute('aria-invalid', 'false');
      }
    }

    updateIndustriesValue() {
      if (!this.industriesField) {
        return;
      }

      const values = Array.from(this.querySelectorAll('[data-pfb-industry]:checked')).map((checkbox) => checkbox.value);
      this.industriesField.value = values.join(', ');
    }

    parseEmailList(value) {
      return String(value || '')
        .split(',')
        .map((email) => email.trim())
        .filter(Boolean);
    }

    buildPayload() {
      this.updateIndustriesValue();

      const data = new FormData(this.form);
      const fields = {};
      const to = this.getAttribute('data-notification-to') || '';
      const cc = this.getAttribute('data-notification-cc') || '';

      data.forEach((value, key) => {
        const normalizedKey = key.replace(/^contact\[/, '').replace(/\]$/, '');
        if (!value || normalizedKey === 'body') {
          return;
        }
        fields[normalizedKey] = value;
      });

      fields['Selected Category'] = this.categorySelect && this.categorySelect.selectedOptions.length
        ? this.categorySelect.selectedOptions[0].textContent.trim()
        : '';

      return {
        to: to,
        cc: cc,
        toEmails: this.parseEmailList(to),
        ccEmails: this.parseEmailList(cc),
        fields: fields
      };
    }

    writeSummary(payload) {
      if (!this.summaryField) {
        return;
      }

      const lines = ['Pongbot business application'];
      Object.keys(payload.fields).forEach((key) => {
        lines.push(`${key}: ${payload.fields[key]}`);
      });
      if (payload.to) {
        lines.push(`Notification To: ${payload.to}`);
      }
      if (payload.cc) {
        lines.push(`Notification CC: ${payload.cc}`);
      }
      this.summaryField.value = lines.join('\n');
    }

    async handleSubmit(event) {
      event.preventDefault();

      const stepsAreValid = [1, 2, 3].every((step) => this.validateStep(step, true));
      if (!stepsAreValid) {
        const invalidPanel = this.panels.find((panel) => panel.querySelector('.is-invalid'));
        if (invalidPanel) {
          this.setStep(Number(invalidPanel.getAttribute('data-pfb-panel')), { focus: false });
        }
        return;
      }

      const payload = this.buildPayload();
      this.writeSummary(payload);

      const endpoint = (this.getAttribute('data-submission-endpoint') || '').trim();
      if (endpoint) {
        try {
          await this.submitToEndpoint(endpoint, payload);
        } catch (error) {
          this.showSubmitError(error);
        }
        return;
      }

      if (this.getAttribute('data-native-submit') === 'true') {
        this.markNativeSubmitIntent();
        this.form.submit();
        return;
      }

      this.showSubmitError(new Error('Submission endpoint is not configured.'));
    }

    showSubmitError(error) {
      if (!this.submitError) {
        return;
      }

      this.submitError.textContent = error && error.message ? error.message : 'Submission failed. Please try again.';
      this.submitError.hidden = false;
      this.submitError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    async submitToEndpoint(endpoint, payload) {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Submission failed: ${response.status}`);
      }

      this.clearDraft();
      this.setStep(4);
    }
  }

  customElements.define('pongbot-business-form', PongbotBusinessForm);
})();
